import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useNavigate} from 'react-router-dom';
import {PLAYER_HOST} from '../../../incase-domain/online';
import {useLogin} from './queries';
import PasswordInput from '../../components/PasswordInput';
import Button from '../../../incase-domain/components/button/Button';
import ConfirmModal from '../../../incase-domain/components/modals/confirmModal/ConfirmModal';
import {type TypedTranslation} from '../../../i18n';

function LoginPage(): JSX.Element {
    const {t} = useTranslation();
    const navigate = useNavigate();
    const loginMutation = useLogin();

    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [presenterModal, setPresenterModal] = useState<boolean>(false);

    const isFormDisabled = !(username && password);

    const loginResult = loginMutation.data;
    const hasLoginError = !!loginResult?.errors;
    const loginErrorMessage = (loginResult?.errors?.message as unknown as TypedTranslation) ?? '';

    useEffect(() => {
        if (loginErrorMessage?.includes('presenterNotAllowedInEditor')) {
            setPresenterModal(true);
        }
    }, [loginErrorMessage]);

    const handleConfirm = (): void => {
        window.location.href = PLAYER_HOST;
    };

    const resetLoginState = (): void => {
        loginMutation.reset();
        setPresenterModal(false);
        setPassword('');
        setUsername('');
    };

    return (
        <>
            <div className="p-login">
                <main>
                    <form
                        className={`c-login__form u-flex u-flex-col u-gap-2 ${hasLoginError && 'is-invalid'}`}
                        onSubmit={(e: React.SyntheticEvent) => {
                            e.preventDefault();
                            if (isFormDisabled) return;
                            loginMutation.mutate({username, password});
                        }}
                    >
                        <div className="p-login__title">{t('loginPage.memberLogin')}</div>
                        <div className="c-input">
                            <input
                                data-cy="username"
                                value={username}
                                onChange={(e) => setUsername(e.target.value.trim())}
                                type="text"
                                placeholder={t('loginPage.usernamePlaceholder')}
                            />
                        </div>
                        <div className="u-flex u-flex-col">
                            <PasswordInput
                                value={password}
                                onChange={(password) => setPassword(password)}
                                placeholder={t('loginPage.passwordPlaceholder')}
                            />
                            <a className="p-login__forgotPassword" href="/forgotPassword">
                                {t('loginPage.forgotPassword')}
                            </a>
                        </div>
                        {loginErrorMessage?.includes('invalidPassword') ? (
                            <>
                                <div className="c-form-help is-invalid">{t(loginErrorMessage)}</div>
                                <div className="c-field">
                                    <Button
                                        disabled={isFormDisabled}
                                        variant="solid"
                                        size="sm"
                                        color="secondary"
                                        onClick={() => navigate('/forgotPassword', {state: username})}
                                    >
                                        {t('loginPage.changeInvalidPassword')}
                                    </Button>
                                </div>
                            </>
                        ) : (
                            <>
                                {loginErrorMessage && <div className="c-form-help is-invalid">{t(loginErrorMessage)}</div>}
                                <div className="c-field">
                                    <Button
                                        dataCy="loginSubmit"
                                        disabled={isFormDisabled}
                                        variant="solid"
                                        size="md"
                                        color="secondary"
                                        type="submit"
                                    >
                                        {t('loginPage.buttonText')}
                                    </Button>
                                </div>
                            </>
                        )}
                    </form>
                </main>
            </div>
            <ConfirmModal
                isOpen={presenterModal}
                onCancel={resetLoginState}
                showCancel
                showConfirm
                onConfirm={handleConfirm}
                color="warning"
                title={t('loginPage.modal.blockPresenter.header')}
                message={t('loginPage.modal.blockPresenter.body')}
            />
        </>
    );
}

export default LoginPage;
